<script setup lang="ts">
import type { CustomerDto, CustomerResponse } from '@/types';
import { nextTick, ref, useTemplateRef, watch } from 'vue';
import axios from 'axios';
import InputCardComponent from '@/components/InputCardComponent.vue';

const customerForm = defineModel<Partial<CustomerDto>>({ required: true })

const props = defineProps<{
  // A customer was found by the email, so name and phone are theirs and can't be changed. The email can, that is the way out
  locked?: boolean
}>()

const emailField = useTemplateRef('emailField')
const nameField = useTemplateRef('nameField')
const mobileField = useTemplateRef('mobileField')

const emit = defineEmits<{
  found: [customer: CustomerResponse]
  // The email isn't, or is no longer, a customer's. CustomerView unlocks and empties the address if it came from them
  notFound: []
}>()

const rules = ref({
  required: (value: unknown) => !!value || "Påkrævet",
  name: (value: string) =>
    (!!value &&
      value.split(" ").length >= 2 &&
      value
        .split(" ")
        .every((x) => /^[a-zA-Z0-9\u00c0-\u017e.:/-]+$/.test(x))) ||
    "Skal bestå af minimum 2 navne.",
  email: (value: string) =>
    /^[^\W_]+(?!.*([\W_]{2}|[\W_]@|@[\W_]))[\w-.]*@[\w-.]+\.[a-z]{2,5}$/i.test(value) ||
    "Skal indeholde et @ og slutte på .dk, .com e.lign.",
  mobile: (value: string) =>
    /^[\d]{8}$/.test(value) ||
    "Må kun indeholde tal og skal være 8 cifre.",
})

async function getCustomer(focused: boolean) {
  const email = customerForm.value.email

  if (!focused && email && rules.value.email(email) === true) {
    try {
      const { data } = await axios.get<CustomerResponse[]>(`proxy/customers?email=${email}`)

      // The email was changed while waiting, so this answer is for an old one
      if (customerForm.value.email !== email) {
        return
      }

      if (data.length) {
        // Name and phone are filled here, the address by AddressInfoComponent (through CustomerView)
        customerForm.value.name = data[0].name
        customerForm.value.mobile = data[0].mobile
        emit('found', data[0])
      }
      else {
        forgetCustomer()
      }
    } catch (error) {
      console.error(error)
    }
  }
}

// Name and phone filled from a found customer belong to that email, so they are emptied when it no longer applies.
// What was typed by hand (nothing found, so not locked) is kept
function forgetCustomer() {
  if (props.locked) {
    customerForm.value.name = undefined
    customerForm.value.mobile = undefined
  }

  emit('notFound')
}

// Typing in, or clearing, the email lets go of a found customer right away. The lookup runs again when leaving the field
function onEmailChange() {
  if (props.locked) {
    forgetCustomer()
  }
}

// The "Ryd" button: empties email, name and phone, and lets go of a found customer (and their address)
function clearAll() {
  forgetCustomer()
  customerForm.value.email = undefined
  customerForm.value.name = undefined
  customerForm.value.mobile = undefined
  resetValidation(true)
}

// Emptied fields look untouched (no "Påkrævet" yet) but still keep Next disabled. Waits a tick, so the fields have
// their new value, and their rules back after being unlocked
async function resetValidation(withEmail = false) {
  await nextTick()

  if (withEmail) {
    emailField.value?.resetValidation()
  }

  nameField.value?.resetValidation()
  mobileField.value?.resetValidation()
}

// The rules are off while locked, since a saved customer that doesn't match them couldn't be fixed and would block Next
watch(() => props.locked, locked => {
  if (!locked) {
    resetValidation()
  }
})
</script>

<template>
  <InputCardComponent label="Kontaktoplysninger">
    <template #actions>
      <v-btn
        prepend-icon="mdi-filter-remove-outline"
        text="Ryd"
        variant="text"
        size="small"
        @click="clearAll"
      />
    </template>

    <v-text-field
      ref="emailField"
      v-model="customerForm.email"
      label="Email"
      name="email"
      type="email"
      :rules="[rules.required, rules.email]"
      clearable
      @update:focused="getCustomer"
      @update:model-value="onEmailChange"
    />
    <v-text-field
      ref="nameField"
      v-model="customerForm.name"
      label="Navn"
      name="name"
      type="text"
      :rules="locked ? [] : [rules.required, rules.name]"
      :disabled="locked"
      :clearable="!locked"
      :append-inner-icon="locked ? 'mdi-lock-outline' : undefined"
    />
    <v-text-field
      ref="mobileField"
      v-model="customerForm.mobile"
      label="Telefon"
      prefix="+45"
      name="mobile"
      type="tel"
      :rules="locked ? [] : [rules.required, rules.mobile]"
      :disabled="locked"
      :clearable="!locked"
      :append-inner-icon="locked ? 'mdi-lock-outline' : undefined"
    />

    <v-alert
      v-if="locked"
      type="info"
      variant="tonal"
      density="compact"
      text="Navn og telefon er hentet ud fra din email. Skift email for at bruge andre oplysninger."
    />
  </InputCardComponent>
</template>
